import { useMemo, useState } from 'react';
import { track } from '../lib/analytics.js';
import { currentLocale, t, useLocale } from '../lib/i18n.js';

interface Props {
  challenge?: string;
  correct: boolean;
  pct: number | null;
  points: number;
  practice?: boolean;
}
export function ShareResult({ challenge, correct, pct, points, practice }: Props) {
  useLocale();
  const [status, setStatus] = useState('');
  const [manual, setManual] = useState(false);
  const referral = useMemo(() => crypto.randomUUID().replaceAll('-', '').slice(0, 12), []);
  const url = new URL(challenge ? '/solo' : '/', location.origin);
  if (challenge) url.searchParams.set('c', challenge);
  url.searchParams.set('ref', referral);
  url.searchParams.set('lang', currentLocale());
  const result =
    correct && pct !== null
      ? t(
          `I got it at ${pct}% complete (+${points} points).`,
          `完成率 ${pct}% で正解！ +${points} 点。`,
        )
      : t('Can you catch the picture before it’s complete?', '絵が完成する前に当てられる？');
  const text = `${practice ? t('Practice round · ', '練習ラウンド · ') : ''}${result}\n${t('Listen & Draw — hear the clues, then tap your answer.', 'Listen & Draw — 英語を聞いて、わかったら回答。')}`;
  const payload = `${text}\n${url.href}`;
  const props = { ...(challenge ? { challenge } : {}), referral };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(payload);
      setStatus(t('Copied. Send it to a friend!', 'コピーしました。友だちに送ろう！'));
      track('share_copy', props);
    } catch {
      setManual(true);
      setStatus(
        t('Select and copy your challenge below.', '下のテキストを選択してコピーしてください。'),
      );
    }
  };
  const share = async () => {
    track('share_click', props);
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Listen & Draw', text, url: url.href });
        return;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
      }
    }
    await copy();
  };
  return (
    <div className="share-result">
      <div className="row">
        <button className="btn btn--primary" onClick={() => void share()}>
          {t('Challenge a friend ↗', '友だちに挑戦状 ↗')}
        </button>
        <button className="btn btn--ghost" onClick={() => void copy()}>
          {t('Copy result', '結果をコピー')}
        </button>
      </div>
      <p className="small muted" role="status">
        {status ||
          t(
            challenge
              ? 'The answer stays secret. Your friend gets the same challenge.'
              : 'Invite a friend to try their own listening challenge.',
            challenge
              ? '正解は秘密。同じ問題を友だちに渡せます。'
              : '正解を明かさず、ゲームを紹介できます。',
          )}
      </p>
      {manual && (
        <textarea
          readOnly
          value={payload}
          aria-label={t('Challenge to copy', 'コピーする挑戦状')}
          onFocus={(e) => e.target.select()}
        />
      )}
    </div>
  );
}
